import {
  NotificationModel,
  NotificationTypes,
  useGetNotifications,
  useReadNotification,
  useReadPushNotification,
} from '@/services/notifications';
import { useCallback, useEffect, useMemo } from 'react';

import { DateTime } from '@/utility/luxon';
import { PATHS } from '@/constants/routes';
import shallow from 'zustand/shallow';
import { useAuth, useDialog } from '../state';
import { useIntl } from 'react-intl';
import { useMe } from '@/services';
import { useNotificationGetOrder } from '../services/my-booking';
import { useRouter } from 'next/router';
import { useUnreadNotifications } from '../state/unread-notifications';
import { useQueryClient } from '@tanstack/react-query';

export enum _DurationUnits {
  year = 'year',
  month = 'month',
  // week = 'week', disabled for now
  day = 'day',
  hour = 'hour',
  minute = 'minute',
  second = 'second',
}

interface useNotificationsProps {
  handleClose?: () => void;
}
export const useNotifications = (useNotificationsProps?: useNotificationsProps) => {
  const [user, setUser] = useAuth(s => [s.user, s.setUser], shallow);
  const [unreadNotifications, setUnreadNotifications] = useUnreadNotifications(
    s => [s.count, s.setUnreadNotifications],
    shallow
  );
  const intl = useIntl();
  const { push } = useRouter();
  const notificationGetOrder = useNotificationGetOrder();
  const queryClient = useQueryClient();
  const [dialog] = useDialog(s => [s.dialog], shallow);

  const invalidateUnreaedNotificationsCount = (): Promise<void> =>
    queryClient.invalidateQueries([{ url: 'notifications/unread/count' }]);

  const { data: userData } = useMe(
    {},
    {
      enabled: !!user?.token,
    }
  );
  const refetchMe = (): Promise<void> =>
    queryClient.invalidateQueries([{ url: 'auth/me' }]);

  useEffect(() => {
    if (user?.token && userData) {
      const { language: _, ...restUserData } = userData.user;
      setUser(prevState => ({
        ...prevState,
        ...restUserData,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    if (user?.token && dialog?.isNotificationPopover) {
      refetch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, dialog?.isNotificationPopover]);

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch } =
    useGetNotifications(
      {
        params: { page: 1, limit: 10 },
      },
      {
        enabled: false,
      }
    );

  const { mutate: readNotification } = useReadNotification(
    {},
    {
      onSuccess: data => {
        refetch();
        setUnreadNotifications(data.count);
      },
    }
  );

  const { mutate: readPushNotification } = useReadPushNotification(
    {},
    {
      onSuccess: () => {
        refetch();
        refetchMe();
      },
    }
  );

  const notifications = useMemo(
    () => (data?.pages ? data?.pages?.flatMap(group => group.data) : []),
    [data]
  );

  /**
   * It returns true if the user has no unread notifications
   * @returns A boolean value.
   */
  const isAllNotificationsAreRead = (): boolean => {
    return unreadNotifications === 0;
  };

  /**
   * "Get the difference between two dates in a specific unit of time."
   *
   * The first two parameters are required. The third parameter is optional
   * @param {DateTime} start - DateTime - The start date
   * @param {DateTime} end - DateTime - The end date
   * @param {_DurationUnits} [type] - _DurationUnits
   * @returns A number
   */
  const getDiffBetweenDates = (
    start: DateTime,
    end: DateTime,
    type?: _DurationUnits
  ): number => {
    const diff = start.diff(end, type).toObject();
    return Math.floor(Number(diff[`${type}s`]));
  };

  const calculateNotificationTime = useCallback(
    /* Calculating the time difference between the current time and the notification time. */
    (date: string): string => {
      const currentTime = DateTime.now();
      const notificationTime = DateTime.fromISO(date);

      for (const unit in _DurationUnits) {
        const strUnit: _DurationUnits = unit.toString() as _DurationUnits;

        const interval = getDiffBetweenDates(currentTime, notificationTime, strUnit);
        if (interval >= 1) {
          return intl.formatMessage(
            {
              id: `notifications.time.${strUnit}`,
            },
            {
              interval: interval,
            }
          );
        }
      }
    },
    [intl]
  );

  /**
   * It takes an objectId, gets the order from the database, gets the booking time from the order, gets
   * the current time, and returns the difference between the booking time and the current time in
   * seconds
   * @param {string} objectId - string - The id of the order you want to check
   * @returns The difference between the current time and the booking time in seconds.
   */
  const checkOrderDate = (objectId: string): number => {
    const currentTime = DateTime.now();
    notificationGetOrder.safeMutateAsync({
      query: {
        id: objectId,
      },
    });
    const bookingTime = DateTime.fromISO(
      notificationGetOrder?.data?.order?.session?.showtime
    );

    const strUnit: _DurationUnits = 'second'.toString() as _DurationUnits;

    return getDiffBetweenDates(currentTime, bookingTime, strUnit);
  };

  /**
   * It takes in a notification object and based on the type of notification, it will redirect the user
   * to the appropriate page
   * @param  - id - The id of the notification
   */
  const handleNotificationClick = ({
    id,
    type,
    objectId,
    objectUniqueName,
    isRead,
    externalLink,
    isPush = false,
    templateNotificationId,
  }: Partial<NotificationModel>): void => {
    if (
      type === NotificationTypes.WELCOME ||
      type === NotificationTypes.GENERAL ||
      type === NotificationTypes.ANNIVERSARY
    )
      push('/');
    if (type === NotificationTypes.FILM || type === NotificationTypes.UPCOMING_FILM)
      push(`/movies/${objectUniqueName}`);
    if (
      type === NotificationTypes.ORDER_REMINDER ||
      type === NotificationTypes.GENERATE_TICKET
    ) {
      push(
        `/user-area/${
          checkOrderDate(objectId) <= 0 ? 'upcoming-booking' : 'view-history'
        }/${objectId}`
      );
    }
    if (type === NotificationTypes.OFFER) {
      push(`/offers/${objectUniqueName}`);
    }

    if (type === NotificationTypes.EXTERNAL_LINK || type === NotificationTypes.SURVEY)
      window.open(externalLink, '_blank').focus();

    if (
      type === NotificationTypes.EXPIRED_EWALLET_TRANSACTION ||
      type === NotificationTypes.REFUND_TRANSACTION
    ) {
      push(PATHS.USER_AREA_WALLET);
    }
    if (type === NotificationTypes.EXPIRED_CASHBACK_WALLET_TRANSACTION) {
      push(PATHS.USER_AREA_CASHBACK);
    }

    useNotificationsProps?.handleClose();
    if (user && user?.token) {
      if (!!isPush) {
        if (!!id) {
          handleReadNotification(id);
        } else {
          handleReadPushNotification(templateNotificationId);
        }
      } else {
        !isRead && handleReadNotification(id);
      }
    }
  };

  /**
   * HandleReadNotification is a function that takes an id of type number and returns nothing.
   * @param {number} id - The id of the notification to be read.
   */
  const handleReadNotification = (id: string): void => {
    readNotification({ query: { id } });
  };

  const handleReadPushNotification = (id: number): void => {
    readPushNotification({ query: { id } });
  };

  return {
    notifications,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    calculateNotificationTime,
    fetchNextPage,
    refetch,
    isAllNotificationsAreRead,
    invalidateUnreaedNotificationsCount,
    getDiffBetweenDates,
    handleNotificationClick,
  };
};
